import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Play, Clock, Sparkles, Lock, CheckCircle2 } from 'lucide-react';

/**
 * V72 — SoulAI's follow-up after a live class she booked and didn't join: the closest
 * recorded class from her own safe library. It stays here whether or not she tapped the
 * notification, and `?video=` from the push auto-opens it.
 */
export const MissedClassCard = ({ refreshKey, watchPct = 90, onPlay, onUnlock }) => {
  const [rec, setRec] = useState(null);

  const fetchRec = useCallback(() => {
    api.get('/api/prenatal-coach/recommendations/latest')
      .then((r) => setRec(r.data?.recommendation || null))
      .catch(() => setRec(null));
  }, []);

  useEffect(() => { fetchRec(); }, [fetchRec, refreshKey]);

  // Deep link from the push: open the player as soon as the class is known.
  useEffect(() => {
    if (!rec || rec.locked) return;
    const wanted = new URLSearchParams(window.location.search).get('video');
    if (wanted && wanted === rec.video_id) {
      onPlay?.({ video_id: rec.video_id, title: rec.title, duration_minutes: rec.duration_minutes, watch_pct: rec.watch_pct });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [rec]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rec) return null;
  const done = rec.status === 'completed' || rec.watch_pct >= watchPct;

  return (
    <Card className={`rounded-2xl ${rec.locked ? 'bg-muted/30 border-dashed' : 'border-primary/30 bg-gradient-to-br from-primary/5 to-rose-50/40'}`}
      data-testid="coach-missed-class-card">
      <CardContent className="pt-4 pb-4 flex items-center gap-3 flex-wrap">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${rec.locked ? 'bg-muted' : 'bg-primary/10'}`}>
          {done ? <CheckCircle2 className="h-4 w-4 text-emerald-700" />
            : rec.locked ? <Lock className="h-3.5 w-3.5 text-muted-foreground" />
              : <Sparkles className="h-4 w-4 text-primary" />}
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            {rec.copy?.title || 'A practice from your live class'}
          </p>
          <p className="text-sm font-medium truncate" data-testid="coach-missed-class-title">{rec.title}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {rec.intensity && (
              <Badge className={`text-[10px] ${rec.intensity === 'moderate' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                {rec.intensity}
              </Badge>
            )}
            {rec.duration_minutes && (
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{rec.duration_minutes} min</span>
            )}
          </div>
          {rec.copy?.body && <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{rec.copy.body}</p>}
          {!done && rec.watch_pct > 0 && (
            <div className="flex items-center gap-2 mt-1.5">
              <Progress value={rec.watch_pct} className="h-1 w-28" />
              <span className="text-[10px] text-muted-foreground">{rec.watch_pct}% watched</span>
            </div>
          )}
        </div>
        <div className="shrink-0">
          {done ? null : rec.locked ? (
            <Button size="sm" variant="outline" className="h-8 text-muted-foreground" onClick={onUnlock}
              data-testid="coach-missed-class-unlock"><Lock className="h-3.5 w-3.5 mr-1" />Unlock</Button>
          ) : (
            <Button size="sm" className="h-8" data-testid="coach-missed-class-play"
              onClick={() => onPlay?.({ video_id: rec.video_id, title: rec.title, duration_minutes: rec.duration_minutes, watch_pct: rec.watch_pct })}>
              <Play className="h-3.5 w-3.5 mr-1" />Play
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
